import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './index.css';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme/theme';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Contact from './components/Contact';
import Blog from './components/Blog';
import Footer from './components/Footer';
import PartnershipSlideshow from './components/PartnershipSlideshow';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ServiceDetails from './pages/ServiceDetails';
import BlogPost from './pages/BlogPost';
import VMwareLanding from './pages/landing/VMwareLanding';
import NutanixLanding from './pages/landing/NutanixLanding';
import VeeamLanding from './pages/landing/VeeamLanding';
import HPEGreenLakeLanding from './pages/landing/HPEGreenLakeLanding';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import NotFound from './pages/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import NetworkStatus from './components/NetworkStatus';
import PullToRefresh from './components/PullToRefresh';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollProgress from './components/ScrollProgress';
import ParticleBackground from './components/ParticleBackground';
import BackToTop from './components/BackToTop';

// Component to handle scroll restoration
function ScrollRestoration() {
  const location = useLocation();

  useEffect(() => {
    const scrollPositions: { [key: string]: number } = JSON.parse(
      sessionStorage.getItem('scrollPositions') || '{}'
    );

    // Restore scroll position if navigating back
    if (scrollPositions[location.key]) {
      setTimeout(() => {
        window.scrollTo(0, scrollPositions[location.key]);
      }, 0);
    } else {
      window.scrollTo(0, 0);
    }

    // Save scroll position before leaving
    return () => {
      scrollPositions[location.key] = window.scrollY;
      sessionStorage.setItem('scrollPositions', JSON.stringify(scrollPositions));
    };
  }, [location]);

  return null;
}


function HomePage() {
  return (
    <main>
      <Hero />
      <Services />
      <About />
      <PartnershipSlideshow />
      <Blog />
      <Contact />
    </main>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    window.location.reload();
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <LoadingSpinner loading={loading} />
        <ScrollProgress />
        <ParticleBackground />
        <Router>
          <ScrollRestoration />
          <PullToRefresh onRefresh={handleRefresh}>
            <div className="App">
              <Header />
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/services/:serviceId" element={<ServiceDetails />} />
                  <Route path="/blog/:blogId" element={<BlogPost />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/landing/vmware" element={<VMwareLanding />} />
                  <Route path="/landing/nutanix" element={<NutanixLanding />} />
                  <Route path="/landing/veeam" element={<VeeamLanding />} />
                  <Route path="/landing/hpe-greenlake" element={<HPEGreenLakeLanding />} />
                  <Route path="/success-stories" element={<SuccessStoriesPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ErrorBoundary>
              <Footer />
              <NetworkStatus />
              <BackToTop />
            </div>
          </PullToRefresh>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;